import { useEffect } from "react";
import { useLang } from "@/lib/i18n";

const SITE_URL = "https://metodo-tao.com";
const DEFAULT_OG = "https://customer-assets.emergentagent.com/job_acompanamiento-tao/artifacts/67x19b64_LAVANGUARDIA%20-%20EMPRESAS%2026--F2-RETRATOS-0259.jpg";

/**
 * Pick the localized value from either a plain string or a {es, ca} object.
 * Falls back to ES if the chosen lang is missing.
 */
const localize = (value, lang) => {
  if (value == null) return value;
  if (typeof value === "object" && !Array.isArray(value)) {
    return value[lang] ?? value.es ?? value[Object.keys(value)[0]];
  }
  return value;
};

const setOrCreateMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const match = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setOrCreateLink = (rel, href, extra = {}) => {
  // Find any link with this rel + matching hreflang (if provided)
  const selector = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Lightweight SEO hook for SPA route changes.
 *
 * `title` and `description` may be strings (mono-lingual) OR localized
 * objects of the form `{ es: "...", ca: "..." }`. The hook auto-picks
 * the active language and falls back to ES if a translation is missing.
 *
 * `article` (optional): when present, marks the page as an `og:type=article`
 * and adds rich `article:*` meta (published time, author, section, tags) so
 * LinkedIn / Facebook show byline + date in the preview card.
 *
 * Side-effects per route change:
 *   - sets <title> + meta description + canonical + OG / Twitter cards
 *   - sets <link rel="alternate" hreflang="..."> for ES, CA and x-default
 *   - sets <meta property="og:locale"> based on current language
 */
export const useSeo = ({ title, description, path = "/", image, imageAlt, article }) => {
  const lang = useLang();

  useEffect(() => {
    // Intentional deps: `title`, `description`, `path`, `image`, `imageAlt`,
    // `article`, `lang`. Module-level helpers and constants are stable.
    const localizedTitle = localize(title, lang);
    const localizedDesc = localize(description, lang);
    const url = `${SITE_URL}${path}`;
    const ogImage = image
      ? image.startsWith("http")
        ? image
        : `${SITE_URL}${image}`
      : DEFAULT_OG;

    if (localizedTitle) document.title = localizedTitle;

    if (localizedDesc) setOrCreateMeta('meta[name="description"]', "content", localizedDesc);
    setOrCreateMeta('meta[property="og:title"]', "content", localizedTitle || "");
    setOrCreateMeta('meta[property="og:description"]', "content", localizedDesc || "");
    setOrCreateMeta('meta[property="og:url"]', "content", url);
    setOrCreateMeta('meta[property="og:image"]', "content", ogImage);
    setOrCreateMeta('meta[property="og:image:width"]', "content", "1200");
    setOrCreateMeta('meta[property="og:image:height"]', "content", "630");
    setOrCreateMeta('meta[property="og:image:alt"]', "content", imageAlt || localizedTitle || "Método TAO");
    setOrCreateMeta('meta[property="og:site_name"]', "content", "Método TAO by Marta");
    setOrCreateMeta('meta[property="og:type"]', "content", article ? "article" : "website");
    setOrCreateMeta(
      'meta[property="og:locale"]',
      "content",
      lang === "ca" ? "ca_ES" : "es_ES"
    );
    setOrCreateMeta(
      'meta[property="og:locale:alternate"]',
      "content",
      lang === "ca" ? "es_ES" : "ca_ES"
    );

    // Rich article meta (LinkedIn, Facebook, Telegram preview cards)
    if (article) {
      if (article.publishedTime) {
        setOrCreateMeta('meta[property="article:published_time"]', "content", article.publishedTime);
      }
      setOrCreateMeta('meta[property="article:author"]', "content", article.author || "Marta Montardit");
      if (article.section) {
        setOrCreateMeta('meta[property="article:section"]', "content", article.section);
      }
      // Up to 6 tags — crawlers cap around that
      (article.tags || []).slice(0, 6).forEach((tag, i) => {
        setOrCreateMeta(`meta[property="article:tag"][data-i="${i}"]`, "content", tag);
        const el = document.head.querySelector(`meta[property="article:tag"][data-i="${i}"]`);
        if (el) el.setAttribute("data-i", String(i));
      });
    }

    setOrCreateMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setOrCreateMeta('meta[name="twitter:title"]', "content", localizedTitle || "");
    setOrCreateMeta('meta[name="twitter:description"]', "content", localizedDesc || "");
    setOrCreateMeta('meta[name="twitter:image"]', "content", ogImage);
    setOrCreateMeta('meta[name="twitter:image:alt"]', "content", imageAlt || localizedTitle || "Método TAO");

    // Canonical
    setOrCreateLink("canonical", url);

    // hreflang: this site switches language via client-side state, not via
    // distinct URLs per language, so the canonical URL already is the single
    // language-agnostic version. Declaring hreflang="es" + "ca" pointing to
    // the same URL creates a "conflicting hreflang" warning in SEO audits
    // because hreflang is designed for different URLs per language. We only
    // publish x-default — the crawler understands the page is multilingual
    // without ambiguity, and we clean up any legacy es/ca tags left in the
    // DOM from previous navigations.
    document.querySelectorAll('link[rel="alternate"][hreflang="es"], link[rel="alternate"][hreflang="ca"]').forEach((el) => el.remove());
    setOrCreateLink("alternate", url, { hreflang: "x-default" });
  }, [title, description, path, image, imageAlt, article, lang]);
};

export default useSeo;
